import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Stack,
  Tooltip
} from "../../components";
import { notification } from "../../services";
import ImagemCropper from "../imageCropper/ImageCropper.component";

const DEFAULT_MAX_IMAGES = 5;
const notFoundImage = "https://public.siafevolution.com.br/not-found.png";

const MultiImageMedia = ({
  useValue,
  setUseValue,
  entity,
  maxImages = DEFAULT_MAX_IMAGES,
  viewMode = "carousel",
  disabled = false,
}) => {
  const [images, setImages] = useState(useValue.imagens || []);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isCropping, setIsCropping] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const fileInputRef = useRef();
  const fileInputCameraRef = useRef();

  useEffect(() => {
    if (JSON.stringify(useValue.imagens) !== JSON.stringify(images)) {
      setImages(useValue.imagens || []);
      setCurrentIndex(0);
    }
  }, [useValue.imagens]);

  const handleFileSelect = (event) => {
    const file =
      event.target.files[0] ||
      (event.dataTransfer && event.dataTransfer.files[0]);
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setCropSrc(reader.result);
      setIsCropping(true);
    };
    reader.readAsDataURL(file);
  };

  const handleAddImage = async () => {
    if (images.length >= maxImages) return;
    const retornoAlerta = await notification.confirmacaoGenericos({
      title: "Selecione uma opção",
      icon: "info",
      confirmButtonText: `
        <i class="ph-fill ph-camera" style="font-size:40px; color: white;"></i> <p style="margin: 0">Câmera</p>
      `,
      cancelButtonText: `
        <i class="ph ph-export" style="font-size:40px; color: black;"></i> <p style="margin: 0">Dispositivo</p>
      `,
      showCancelButton: true,
      showCloseButton: true,
    });
    setEditingIndex(null);
    if (retornoAlerta.isConfirmed) {
      fileInputCameraRef.current.click();
      return;
    }
    if (retornoAlerta.dismiss === "cancel") {
      fileInputRef.current.click();
      return;
    }
  };

  const handleEditImage = () => {
    setEditingIndex(currentIndex);
    setCropSrc(images[currentIndex]);
    setIsCropping(true);
  };

  const handleRemoveImage = () => {
    const newImages = images.filter((_, i) => i !== currentIndex);
    setImages(newImages);
    setUseValue({ ...useValue, imagens: newImages });
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : 0));
  };

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : prev));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev < images.length - 1 ? prev + 1 : prev));
  };

  const onSaveCrop = (croppedDataUrl) => {
    let newImages;
    if (editingIndex === null) {
      if (images.length < maxImages) {
        newImages = [...images, croppedDataUrl];
        setCurrentIndex(newImages.length - 1);
      } else {
        return;
      }
    } else {
      newImages = [...images];
      newImages[editingIndex] = croppedDataUrl;
    }
    setImages(newImages);
    if (entity.setImagens) entity.setImagens(newImages);
    setUseValue({ ...useValue, imagens: newImages });
    setIsCropping(false);
    setCropSrc(null);
    setEditingIndex(null);
  };

  const onHideCropper = () => {
    setIsCropping(false);
    setCropSrc(null);
    setEditingIndex(null);
  };

  const ultimaImagem =
    currentIndex === images.length - 1 || images.length === 0;

  const isSequenceMode = viewMode === "sequence";

  const renderActionButton = ({
    title,
    onClick,
    iconClass,
    disabled = false,
  }) => (
    <Tooltip title={title} arrow placement="top">
      <span>
        <IconButton
          size="small"
          onClick={onClick}
          disabled={disabled}
          sx={{
            backgroundColor: "#EDEEF0",
            "&:hover": { backgroundColor: "#B6BABC" },
            fontSize: "14px",
          }}
        >
          <i className={iconClass} />
        </IconButton>
      </span>
    </Tooltip>
  );

  return (
    <>
      <input
        accept="image/*"
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleFileSelect}
      />

      <input
        accept="image/*"
        type="file"
        capture="environment"
        ref={fileInputCameraRef}
        style={{ display: "none" }}
        onChange={handleFileSelect}
      />

      {isSequenceMode ? (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            alignItems: "flex-start",
          }}
        >
          {images.map((image, index) => (
            <Box
              key={`image-${index}`}
              sx={{
                width: 160,
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
                overflow: "hidden",
                backgroundColor: "#fff",
              }}
            >
              <img
                src={image || notFoundImage}
                alt={`Imagem ${index + 1}`}
                style={{
                  width: "100%",
                  height: 120,
                  objectFit: "cover",
                  display: "block",
                }}
              />

              <Stack
                direction="row"
                justifyContent="center"
                spacing={1}
                sx={{ p: 1 }}
              >
                {renderActionButton({
                  title: "Recortar",
                  iconClass: "ph ph-scissors",
                  disabled,
                  onClick: () => {
                    setEditingIndex(index);
                    setCropSrc(images[index]);
                    setIsCropping(true);
                  },
                })}
                {renderActionButton({
                  title: "Excluir",
                  iconClass: "ph-fill ph-trash",
                  disabled,
                  onClick: () => {
                    setCurrentIndex(index);
                    const newImages = images.filter((_, i) => i !== index);
                    setImages(newImages);
                    if (entity.setImagens) entity.setImagens(newImages);
                    setUseValue({ ...useValue, imagens: newImages });
                    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : 0));
                  },
                })}
              </Stack>
            </Box>
          ))}

          {images.length < maxImages && !disabled && (
            <Button
              variant="outlined"
              color="primary"
              onClick={handleAddImage}
              sx={{
                width: 160,
                height: 160,
                borderStyle: "dashed",
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
              }}
            >
              <i
                className="ph-bold ph-upload-simple"
                style={{ fontSize: 18 }}
              />
              Adicionar
            </Button>
          )}
        </Box>
      ) : (
        <Box
          position="relative"
          sx={{
            width: "100%",
            maxWidth: "200px",
            mx: "2em",
            borderRadius: "3px",
            overflow: "hidden",
          }}
        >
          <img
            className="carousel-image"
            src={images[currentIndex] || notFoundImage}
            alt={`Imagem ${currentIndex + 1}`}
          />

          <Box
            position="absolute"
            top={8}
            right={8}
            display="flex"
            flexDirection="column"
            gap={1}
          >
            {images.length < maxImages &&
              ultimaImagem &&
              !disabled &&
              renderActionButton({
                title: "Adicionar imagem",
                iconClass: "ph-bold ph-upload-simple",
                onClick: handleAddImage,
              })}

            {images.length > 0 && (
              <>
                {renderActionButton({
                  title: "Recortar",
                  iconClass: "ph ph-scissors",
                  disabled,
                  onClick: handleEditImage,
                })}
                {renderActionButton({
                  title: "Excluir",
                  iconClass: "ph-fill ph-trash",
                  disabled,
                  onClick: handleRemoveImage,
                })}
              </>
            )}
          </Box>

          <Stack direction="row" justifyContent="center" spacing={2} mt={2}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handlePrev}
              disabled={currentIndex === 0}
              sx={{
                height: "35px",
                borderRadius: "50%",
                minWidth: "15px",
                boxShadow: "none",
              }}
            >
              <i
                className="ph-bold ph-caret-left"
                style={{
                  fontSize: 15,
                  color: currentIndex === 0 ? "#494C62" : "white",
                }}
              ></i>
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleNext}
              disabled={ultimaImagem}
              sx={{
                height: "35px",
                borderRadius: "50%",
                minWidth: "15px",
                boxShadow: "none",
              }}
            >
              <i
                className="ph-bold ph-caret-right"
                style={{
                  fontSize: 15,
                  color: ultimaImagem ? "#494C62" : "white",
                }}
              ></i>
            </Button>
          </Stack>
        </Box>
      )}

      {isCropping && (
        <ImagemCropper
          image={cropSrc}
          open={isCropping}
          onSave={onSaveCrop}
          onHide={onHideCropper}
        />
      )}
    </>
  );
};

export default MultiImageMedia;
